import { createAsyncThunk } from '@reduxjs/toolkit';
import { updateCuePointsSettings } from '../slices/cuePointsSettingsSlice';

const STORAGE_KEY = 'cuePointsSettings_v11';

const DEFAULTS = {
    detectCues: true,
    sensitivity: 0.82,
    minDistanceBars: 16,
    maxHotCues: 8,
    cueMode: 'hot',
    detectLoops: true,
    loopBars: [4, 8, 16, 32],
    maxLoops: 6,
    loopSensitivity: 0.5,
    loopFocus: 'balanced',
    snapToBeat: true,
    // Snap detected cues onto the nearest beatgrid beat (window: 45% of a
    // beat). CUE-DETR's raw positions are tens-to-hundreds of ms off, and
    // loops anchor to cues — so this puts cue AND loop on the beat together.
    quantizeCuesToGrid: true,
    keepOriginalsUntouched: false,
    writeFileTags: true,
    targetRekordbox: true,
    targetSerato: false,
    targetTraktor: false,
    targetVirtualdj: false,
};

// Heal any bad/corrupted persisted values (e.g. maxHotCues stuck at 0 -> no cues)
// so settings always load to something sensible, while keeping valid user choices.
const clampInt = (v, lo, hi, def) => (Number.isInteger(v) && v >= lo && v <= hi ? v : def);
const clampNum = (v, lo, hi, def) => (Number.isFinite(v) && v >= lo && v <= hi ? v : def);
const sanitize = (s) => ({
    ...s,
    sensitivity: clampNum(s.sensitivity, 0.5, 0.99, 0.82),
    minDistanceBars: clampInt(s.minDistanceBars, 1, 64, 16),
    maxHotCues: clampInt(s.maxHotCues, 1, 8, 8),     // at least 1 hot cue, else 0 cues
    maxLoops: clampInt(s.maxLoops, 1, 8, 6),
    loopSensitivity: clampNum(s.loopSensitivity, 0, 1, 0.5),
    loopBars: (Array.isArray(s.loopBars) && s.loopBars.length) ? s.loopBars : [4, 8, 16, 32],
    loopFocus: ['grooves', 'balanced', 'drops'].includes(s.loopFocus) ? s.loopFocus : 'balanced',
    cueMode: s.cueMode === 'memory' ? 'memory' : 'hot',
    quantizeCuesToGrid: s.quantizeCuesToGrid === true,
});

export const loadCuePointsSettings = createAsyncThunk(
    'cuePointsSettings/load',
    async (_, { dispatch }) => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const saved = raw ? JSON.parse(raw) : null;
            dispatch(updateCuePointsSettings(sanitize({ ...DEFAULTS, ...saved })));
        } catch (error) {
            console.error('Error loading cue point settings:', error);
        }
    }
);

export const saveCuePointsSettings = createAsyncThunk(
    'cuePointsSettings/save',
    async (settings) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        } catch (error) {
            console.error('Error saving cue point settings:', error);
        }
    }
);
